import { type CombinedError, mapExchange } from "urql";

const traceparentHeaderName = "traceparent";
const cloudTraceHeaderName = "x-cloud-trace-context";
const requestIDHeaderName = "x-request-id";
const correlationIDHeaderName = "x-correlation-id";
const traceIDHexLength = 32;
const spanIDHexLength = 16;

export type GraphQLTraceMetadata = {
  trace_id?: string;
  span_id?: string;
  request_id?: string;
  correlation_id?: string;
};

export type GraphQLErrorMetadata = {
  operation_name?: string;
  operation_kind?: string;
  response_status?: number;
  error_count?: number;
  error_codes?: string[];
  error_paths?: string[];
  network_error_name?: string;
  network_error_status?: number;
};

const operationMetadataByError = new WeakMap<
  CombinedError,
  Pick<GraphQLErrorMetadata, "operation_name" | "operation_kind">
>();

export const traceContextExchange = mapExchange({
  onResult(result) {
    if (!result.error) return result;
    const definition = result.operation.query.definitions.find(
      (candidate) => candidate.kind === "OperationDefinition",
    );
    if (!definition) return result;
    operationMetadataByError.set(result.error, {
      ...(definition.name?.value
        ? { operation_name: definition.name.value }
        : {}),
      operation_kind: definition.operation,
    });
    return result;
  },
});

/**
 * query、variables、error message を送らず、operation と失敗箇所を
 * CombinedError から Bugsnag 用の診断情報へ変換します。
 */
export function graphQLErrorMetadataFromCombinedError(
  error: CombinedError,
): GraphQLErrorMetadata {
  const metadata: GraphQLErrorMetadata = {
    ...(operationMetadataByError.get(error) ?? {}),
  };
  const response = (error as { response?: unknown }).response;
  if (response instanceof Response) {
    metadata.response_status = response.status;
  }
  if (error.graphQLErrors.length > 0) {
    metadata.error_count = error.graphQLErrors.length;
    const codes = error.graphQLErrors
      .map((graphQLError) => graphQLError.extensions?.code)
      .filter((code): code is string => typeof code === "string")
      .slice(0, 10);
    if (codes.length > 0) {
      metadata.error_codes = codes;
    }
    const paths = error.graphQLErrors
      .map((graphQLError) => graphQLError.path?.join("."))
      .filter((path): path is string => Boolean(path))
      .slice(0, 10);
    if (paths.length > 0) {
      metadata.error_paths = paths;
    }
  }
  if (error.networkError) {
    metadata.network_error_name = error.networkError.name;
    const status = (
      error.networkError as { status?: unknown; statusCode?: unknown }
    ).status;
    const statusCode = (
      error.networkError as { status?: unknown; statusCode?: unknown }
    ).statusCode;
    if (typeof status === "number") {
      metadata.network_error_status = status;
    } else if (typeof statusCode === "number") {
      metadata.network_error_status = statusCode;
    }
  }
  return metadata;
}

/**
 * Bugsnag の Trace Context tab に載せる低カーディナリティな response trace 情報を
 * urql CombinedError から取り出します。Response が無い error では空 object を返します。
 */
export function traceMetadataFromCombinedError(
  error: CombinedError,
): GraphQLTraceMetadata {
  const response = (error as { response?: unknown }).response;
  if (!(response instanceof Response)) {
    return {};
  }
  return traceMetadataFromResponseHeaders(response.headers);
}

/**
 * response headers から frontend Bugsnag metadata に載せてよい trace 情報だけを抽出します。
 * malformed header は無視し、古い request の trace を fallback として使いません。
 */
export function traceMetadataFromResponseHeaders(
  headers: Headers,
): GraphQLTraceMetadata {
  const metadata: GraphQLTraceMetadata = {};
  const [traceID, spanID] = traceContextFromTraceparent(
    headers.get(traceparentHeaderName),
  );
  if (traceID) {
    metadata.trace_id = traceID;
  }
  if (spanID) {
    metadata.span_id = spanID;
  }
  if (!metadata.trace_id) {
    const [cloudTraceID] = traceContextFromCloudTraceContext(
      headers.get(cloudTraceHeaderName),
    );
    if (cloudTraceID) {
      metadata.trace_id = cloudTraceID;
    }
  }
  const requestID = headers.get(requestIDHeaderName);
  if (requestID) {
    metadata.request_id = requestID;
  }
  const correlationID = headers.get(correlationIDHeaderName);
  if (correlationID) {
    metadata.correlation_id = correlationID;
  }
  return metadata;
}

/**
 * 既存 Bugsnag metadata と GraphQL trace metadata を、Trace Context tab を上書きしない形で統合します。
 * `Trace` は Bugsnag の distributed trace 用予約 tab なので使用しません。
 */
export function mergeGraphQLTraceMetadataForBugsnag(
  metadata: Record<string, unknown> | undefined,
  traceMetadata: GraphQLTraceMetadata,
): Record<string, unknown> | undefined {
  if (Object.keys(traceMetadata).length === 0) {
    return metadata;
  }
  return {
    ...(metadata ?? {}),
    "Trace Context": {
      ...((metadata?.["Trace Context"] as
        | Record<string, unknown>
        | undefined) ?? {}),
      ...traceMetadata,
    },
  };
}

/**
 * GraphQL の診断情報を既存の Bugsnag metadata と衝突しない専用領域へ統合します。
 */
export function mergeGraphQLErrorMetadataForBugsnag(
  metadata: Record<string, unknown> | undefined,
  graphQLErrorMetadata: GraphQLErrorMetadata,
): Record<string, unknown> | undefined {
  if (Object.keys(graphQLErrorMetadata).length === 0) {
    return metadata;
  }
  return {
    ...(metadata ?? {}),
    GraphQL: {
      ...((metadata?.GraphQL as Record<string, unknown> | undefined) ?? {}),
      ...graphQLErrorMetadata,
    },
  };
}

/**
 * W3C traceparent から trace ID と span ID を検証しながら取り出します。
 */
function traceContextFromTraceparent(value: string | null): [string, string] {
  const parts = value?.trim().split("-") ?? [];
  if (parts.length !== 4) {
    return ["", ""];
  }
  const traceID = parts[1]?.trim().toLowerCase() ?? "";
  if (!fixedLengthLowerHex(traceID, traceIDHexLength)) {
    return ["", ""];
  }
  const spanID = parts[2]?.trim().toLowerCase() ?? "";
  if (!fixedLengthLowerHex(spanID, spanIDHexLength)) {
    return [traceID, ""];
  }
  return [traceID, spanID];
}

/**
 * X-Cloud-Trace-Context から Cloud Logging trace ID 部分だけを検証して取り出します。
 */
function traceContextFromCloudTraceContext(value: string | null): [string] {
  const traceID = value?.trim().split("/", 1)[0]?.toLowerCase() ?? "";
  if (!fixedLengthLowerHex(traceID, traceIDHexLength)) {
    return [""];
  }
  return [traceID];
}

/**
 * trace header の ID が期待長の lowercase hex だけで構成されるかを検証します。
 */
function fixedLengthLowerHex(value: string, expectedLength: number): boolean {
  return (
    value.length === expectedLength &&
    Array.from(value).every(
      (char) => (char >= "0" && char <= "9") || (char >= "a" && char <= "f"),
    )
  );
}
